/**
 * Special Terms Builder
 * Merges API-provided special terms (quote-specific notes) with
 * standard terms (hardcoded legal text) based on the standardTerms
 * array in the quote payload.
 */
import * as Sentry from '@sentry/browser';
import { html } from 'lit';
import '../components/quote/terms/letterbox-special-terms.js';
import '../components/quote/terms/digital-special-terms.js';
import '../components/quote/terms/data-special-terms.js';
import '../components/quote/terms/election-special-terms.js';

/**
 * Configuration mapping standardTerms values to term components.
 * Each key must match a value from the payload's standardTerms array.
 * Order in this array determines display order after special terms.
 */
const STANDARD_TERMS_CONFIG = [
    {
        key: 'Letterbox',
        title: 'Letterbox Services - Special Terms',
        templateContent: html`<letterbox-special-terms></letterbox-special-terms>`,
    },
    {
        key: 'Digital',
        title: 'Digital Services - Special Terms',
        templateContent: html`<digital-special-terms></digital-special-terms>`,
    },
    {
        key: 'Data',
        title: 'Data Services - Special Terms',
        templateContent: html`<data-special-terms></data-special-terms>`,
    },
    {
        key: 'Election',
        title: 'Election Advertising - Special Terms',
        templateContent: html`<election-special-terms></election-special-terms>`,
    },
];

/**
 * Build the complete terms array by merging API special terms
 * (quote-specific notes) with standard terms (hardcoded legal text).
 *
 * Standard terms seq numbers continue from the highest special term seq,
 * so they sort naturally after special terms in the accordion.
 *
 * Payload fields:
 *   specialTerms - array of {seq, term} objects with quote-specific notes
 *   standardTerms - array of strings referencing hardcoded terms (e.g. ["Letterbox", "Digital"])
 *
 * @param {Object} quoteData - The quote data from the API
 * @returns {Array<{seq: number, term?: string, title?: string, templateContent?: import('lit').TemplateResult}>}
 */
/**
 * General Terms & Conditions — always appears as item #1 in the accordion.
 * Hardcoded; not driven by the API payload.
 */
const GENERAL_TERMS = {
    seq: 0,
    title: 'General Terms & Conditions',
    templateContent: html`<p class="text-sm text-gray-700">
        General Terms & Conditions available to review on the REACH Website at
        <a
            href="https://www.reach.nz/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            class="text-peppermint hover:underline font-medium"
            >https://www.reach.nz/terms-and-conditions</a
        >
    </p>`,
};

export function buildTermsWithSpecialTerms(quoteData) {
    const specialTerms = quoteData?.specialTerms || [];
    const standardTermsKeys = quoteData?.standardTerms || [];

    // Flag any unrecognized keys -- missing legal terms is a compliance issue
    const knownKeys = STANDARD_TERMS_CONFIG.map((c) => c.key);
    const unrecognizedKeys = standardTermsKeys.filter((k) => !knownKeys.includes(k));
    if (unrecognizedKeys.length > 0) {
        Sentry.captureMessage('Unrecognized standardTerms keys in quote payload', {
            level: 'error',
            tags: { feature: 'terms-rendering' },
            extra: {
                unrecognizedKeys,
                allKeys: standardTermsKeys,
                quoteReference: quoteData?.quoteId || quoteData?.quoteReference || null,
            },
        });
    }

    const maxSeq = specialTerms.reduce((max, t) => Math.max(max, t.seq || 0), 0);

    let nextSeq = maxSeq + 1;
    const standardTerms = STANDARD_TERMS_CONFIG.filter((config) =>
        standardTermsKeys.includes(config.key)
    ).map((config) => ({
        seq: nextSeq++,
        title: config.title,
        templateContent: config.templateContent,
    }));

    return [GENERAL_TERMS, ...specialTerms, ...standardTerms];
}
